import axios, { AxiosError } from 'axios';
import attachAuthToken from './axiosConfig';

/**
 * Fotografia da regra (comissionamentos/at-04): o que foi gerado quando a regra nasceu ×
 * o que vale hoje.
 *
 * ⚠️ **Sem nenhum catch silencioso** (regra da at-60): aqui "nenhum boleto saiu" é a
 * afirmação sobre a qual alguém decide se um pagamento foi a maior. Engolir o erro faria
 * falha de rede ter a mesma silhueta de "está tudo certo" numa tela que trata dinheiro.
 * Os erros viram `Error` com a mensagem do backend, e quem decide o que mostrar é a tela.
 */

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  withCredentials: true,
});
attachAuthToken(api);

export type SituacaoBoletoNaFoto = 'MANTIDO' | 'SAIU' | 'ENTROU';

export type MotivoBoleto =
  | 'ESTORNADO'
  | 'CANCELADO'
  | 'FORA_DA_BASE'
  | 'REGRA_EXCLUIDA'
  | 'REGRA_ALTERADA'
  | 'PAGO_DEPOIS_DA_FOTO'
  | 'SEM_MOTIVO_APARENTE'
  | null;

export interface BoletoDoDiff {
  situacao: SituacaoBoletoNaFoto;
  motivo: MotivoBoleto;
  recebimento_id: string;
  entrada_id: string;
  valor_vencimento: string | number | null;
  data_pagamento: string | null;
  termo: string | null;
  associado_nome: string | null;
  situacao_atualizada_em: string | null;
}

/**
 * 🔴 `disponivel: false` significa **a fotografia não está instalada neste ambiente** — o
 * `.sql` do módulo é passo manual e pode não ter sido aplicado. É um TERCEIRO estado, distinto
 * de "não há divergência" e de "falhou ao consultar", e a tela trata os três de formas
 * diferentes. Colapsá-lo em qualquer um dos outros faz a tela mentir.
 */
export interface FotoDaRegra {
  disponivel?: boolean;
  temFoto: boolean;
  foto: {
    periodo: string;
    qtd_entradas: number;
    total_valor_pago: string | number;
    valor_calculado: string | number;
    tirada_em: string;
    origem: string;
    fonte_calculo: string | null;
    contrato_inicio: string | null;
    contrato_fim: string | null;
  } | null;
  agora: { qtdEntradas: number; totalValorPago: string };
  resumo: { mantidos: number; sairam: number; entraram: number };
  boletos: BoletoDoDiff[];
}

export interface DivergenciaDoQualificado {
  periodo: string;
  boletosNaFoto: number;
  boletosQueNaoValemMais: number;
  valorNaFoto: string;
  valorAtual: string;
  delta: string;
  /** Quando `valorAtual` foi calculado. Null = nunca — o cron pode estar parado. */
  valorAtualCalculadoEm: string | null;
}

export async function getFotoDaRegra(regraId: string, periodo: string): Promise<FotoDaRegra> {
  try {
    const { data } = await api.get(`/comissionamentos/regras/${regraId}/foto`, {
      params: { periodo },
    });
    return data as FotoDaRegra;
  } catch (e) {
    const err = e as AxiosError<any>;
    throw new Error(err.response?.data?.message || 'Não foi possível carregar a fotografia da regra');
  }
}

export async function getDivergenciasDoQualificado(
  qualificado_tipo: string,
  qualificado_uuid: string,
): Promise<{ disponivel?: boolean; data: DivergenciaDoQualificado[] }> {
  try {
    const { data } = await api.get('/comissionamentos/qualificado/divergencias', {
      params: { qualificado_tipo, qualificado_uuid },
    });
    return data as { disponivel?: boolean; data: DivergenciaDoQualificado[] };
  } catch (e) {
    const err = e as AxiosError<any>;
    throw new Error(err.response?.data?.message || 'Não foi possível verificar as competências');
  }
}

export interface ExecucaoPipeline {
  id: string;
  iniciado_em: string;
  concluido_em: string | null;
  status: 'EM_ANDAMENTO' | 'SUCESSO' | 'FALHOU';
  disparado_por: string;
  erro: string | null;
}

export interface StatusPipeline {
  /** `false` = o registro de execuções ainda não existe neste ambiente (`.sql` não aplicado). */
  disponivel?: boolean;
  /** Data do último SUCESSO — nunca a da última tentativa (regra do carimbo da at-01). */
  ultimaExecucaoComSucesso: string | null;
  ultimaTentativa: string | null;
  emAndamento: boolean;
  execucoes: ExecucaoPipeline[];
}

export async function getStatusPipeline(): Promise<StatusPipeline> {
  try {
    const { data } = await api.get('/comissionamentos/pipeline/status');
    return data as StatusPipeline;
  } catch (e) {
    const err = e as AxiosError<any>;
    throw new Error(err.response?.data?.message || 'Não foi possível verificar o processamento');
  }
}

export async function dispararPipeline(): Promise<{ disparado: boolean; motivo?: string }> {
  try {
    const { data } = await api.post('/comissionamentos/pipeline/executar');
    return data as { disparado: boolean; motivo?: string };
  } catch (e) {
    const err = e as AxiosError<any>;
    throw new Error(err.response?.data?.message || 'Não foi possível iniciar o processamento');
  }
}
